import { useMemo, useState } from 'react';
import NameTypeahead from './NameTypeahead';
import { GENERATION_OPTIONS, TYPE_OPTIONS } from '../utils/dex';
import {
  CATEGORIES,
  COLORS,
  LETTERS,
  STAT_FIELDS,
  activeChips,
  clearChip,
  emptyFilters,
} from '../utils/dexFilters';
import { formatDexNumber } from '../utils/pokemon';
import { foldName } from '../utils/suggest';

function title(value) {
  return String(value || '').charAt(0).toUpperCase() + String(value || '').slice(1);
}

function toggleItem(list, value) {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

function RangePair({ id, label, min, max, step, low, high, onLow, onHigh }) {
  return (
    <fieldset className="range-pair">
      <legend>{label}</legend>
      <label htmlFor={`${id}-min`}>Min</label>
      <input
        id={`${id}-min`}
        type="number"
        inputMode="decimal"
        min={min}
        max={max}
        step={step}
        value={low}
        onChange={(event) => onLow(event.target.value)}
      />
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={low === '' ? min : low}
        aria-label={`${label} minimum`}
        onChange={(event) => onLow(event.target.value)}
      />
      <label htmlFor={`${id}-max`}>Max</label>
      <input
        id={`${id}-max`}
        type="number"
        inputMode="decimal"
        min={min}
        max={max}
        step={step}
        value={high}
        onChange={(event) => onHigh(event.target.value)}
      />
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={high === '' ? max : high}
        aria-label={`${label} maximum`}
        onChange={(event) => onHigh(event.target.value)}
      />
    </fieldset>
  );
}

function TokenPicker({ id, label, options, selected, mode, onMode, onChange, placeholder }) {
  const [text, setText] = useState('');
  const folded = foldName(text);
  const hits = useMemo(() => {
    if (!folded) return [];
    return options
      .filter((option) => !selected.includes(option.id) && foldName(option.name).includes(folded))
      .slice(0, 8);
  }, [options, selected, folded]);

  return (
    <div className="token-picker">
      <div className="token-head">
        <label htmlFor={id}>{label}</label>
        <div className="mode-toggle" role="group" aria-label={`${label} match`}>
          <button type="button" className={mode === 'any' ? 'choice-btn is-on' : 'choice-btn'} aria-pressed={mode === 'any'} onClick={() => onMode('any')}>Any</button>
          <button type="button" className={mode === 'all' ? 'choice-btn is-on' : 'choice-btn'} aria-pressed={mode === 'all'} onClick={() => onMode('all')}>All</button>
        </div>
      </div>
      <input
        id={id}
        value={text}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(event) => setText(event.target.value)}
      />
      {hits.length > 0 && (
        <ul className="token-hits">
          {hits.map((option) => (
            <li key={option.id}>
              <button
                type="button"
                className="choice-btn"
                onClick={() => {
                  onChange([...selected, option.id]);
                  setText('');
                }}
              >
                {option.name}
              </button>
            </li>
          ))}
        </ul>
      )}
      {selected.length > 0 && (
        <ul className="chip-row">
          {selected.map((idValue) => {
            const option = options.find((item) => item.id === idValue);
            return (
              <li key={idValue}>
                <button type="button" className="filter-chip" onClick={() => onChange(selected.filter((item) => item !== idValue))}>
                  {option?.name || idValue} <span aria-hidden="true">×</span>
                  <span className="sr-only">Remove {option?.name || idValue}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function DexFilterPanel({
  idPrefix,
  filters,
  onChange,
  entries,
  nameUniverse,
  resultCount,
  totalCount,
  learnsets,
  learnState,
  onPick,
  picked = [],
  filterRoot = true,
}) {
  const set = (patch) => onChange({ ...filters, ...patch });
  const chips = activeChips(filters);
  const abilityOptions = useMemo(() => {
    const names = new Set();
    (entries || []).forEach((entry) => {
      (entry.abilities || []).forEach((ability) => {
        if (ability?.name) names.add(ability.name);
      });
    });
    return [...names].sort((a, b) => a.localeCompare(b)).map((name) => ({ id: name, name }));
  }, [entries]);
  const moveOptions = useMemo(() => (
    (learnsets?.ids || []).map((id, index) => ({ id, name: learnsets.names?.[index] || id }))
  ), [learnsets]);

  return (
    <form
      className="filter-panel"
      data-filter-root={filterRoot ? idPrefix : undefined}
      role="search"
      onSubmit={(event) => event.preventDefault()}
    >
      <div className="filter-toolbar">
        <div className="mode-toggle" role="group" aria-label="Filter detail">
          <button
            type="button"
            className={filters.mode === 'basic' ? 'choice-btn is-on' : 'choice-btn'}
            aria-pressed={filters.mode === 'basic'}
            onClick={() => onChange({
              ...emptyFilters('basic'),
              join: filters.join,
              query: filters.query,
              letter: filters.letter,
              types: filters.types,
              typeMode: filters.typeMode,
              generation: filters.generation,
              dexMin: filters.dexMin,
              dexMax: filters.dexMax,
              categories: filters.categories,
              color: filters.color,
              heightMin: filters.heightMin,
              heightMax: filters.heightMax,
              weightMin: filters.weightMin,
              weightMax: filters.weightMax,
              megas: filters.megas,
            })}
          >
            Basic
          </button>
          <button
            type="button"
            className={filters.mode === 'advanced' ? 'choice-btn is-on' : 'choice-btn'}
            aria-pressed={filters.mode === 'advanced'}
            onClick={() => set({ mode: 'advanced' })}
          >
            Advanced
          </button>
        </div>
        <div className="mode-toggle" role="group" aria-label="How filters combine">
          <button
            type="button"
            className={filters.join === 'and' ? 'choice-btn is-on' : 'choice-btn'}
            aria-pressed={filters.join === 'and'}
            onClick={() => set({ join: 'and' })}
          >
            Match all
          </button>
          <button
            type="button"
            className={filters.join === 'or' ? 'choice-btn is-on' : 'choice-btn'}
            aria-pressed={filters.join === 'or'}
            onClick={() => set({ join: 'or' })}
          >
            Match any
          </button>
        </div>
        <p className="filter-count" role="status">
          {resultCount} of {totalCount}
        </p>
        <button type="button" className="btn btn-ghost" onClick={() => onChange(emptyFilters(filters.mode))}>
          Clear all
        </button>
      </div>

      {chips.length > 0 && (
        <ul className="chip-row" aria-label="Active filters">
          {chips.map((chip) => (
            <li key={chip.id}>
              <button type="button" className="filter-chip" onClick={() => onChange(clearChip(filters, chip.id))}>
                <span className="chip-label">{chip.label}</span>
                <span aria-hidden="true"> ×</span>
                <span className="sr-only">Remove {chip.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <NameTypeahead
        id={`${idPrefix}-search`}
        label="Search by name or number"
        entries={nameUniverse || entries}
        query={filters.query}
        onQueryChange={(query) => set({ query })}
        onSelect={(entry) => {
          if (onPick) onPick(entry);
          else if (entry && !entry.unmatched) set({ query: entry.name });
        }}
        placeholder="Pikachu or 25"
      />

      <div className="letter-scroller">
        <div className="letter-row" role="group" aria-label="Starts with">
          {LETTERS.map((letter) => (
            <button
              key={letter}
              type="button"
              className={filters.letter === letter ? 'letter-btn is-on' : 'letter-btn'}
              aria-pressed={filters.letter === letter}
              onClick={() => set({ letter: filters.letter === letter ? '' : letter })}
            >
              {letter}
            </button>
          ))}
        </div>
      </div>

      <div className="type-block">
        <div className="token-head">
          <p className="filter-label" id={`${idPrefix}-types`}>Type</p>
          <div className="mode-toggle" role="group" aria-label="Type match">
            <button type="button" className={filters.typeMode === 'any' ? 'choice-btn is-on' : 'choice-btn'} aria-pressed={filters.typeMode === 'any'} onClick={() => set({ typeMode: 'any' })}>Any</button>
            <button type="button" className={filters.typeMode === 'both' ? 'choice-btn is-on' : 'choice-btn'} aria-pressed={filters.typeMode === 'both'} onClick={() => set({ typeMode: 'both' })}>Both</button>
          </div>
        </div>
        <div className="type-row" role="group" aria-labelledby={`${idPrefix}-types`}>
          {TYPE_OPTIONS.map((type) => (
            <button
              key={type}
              type="button"
              className={filters.types.includes(type) ? 'choice-btn is-on' : 'choice-btn'}
              aria-pressed={filters.types.includes(type)}
              onClick={() => set({ types: toggleItem(filters.types, type) })}
            >
              {title(type)}
            </button>
          ))}
        </div>
      </div>

      <div className="filter-grid">
        <div>
          <label htmlFor={`${idPrefix}-gen`}>Generation</label>
          <select
            id={`${idPrefix}-gen`}
            value={filters.generation}
            onChange={(event) => set({ generation: event.target.value })}
          >
            <option value="all">All regions</option>
            {GENERATION_OPTIONS.map(([value, text]) => (
              <option key={value} value={value}>{text}</option>
            ))}
          </select>
        </div>
        <RangePair
          id={`${idPrefix}-dex`}
          label="Dex number"
          min={1}
          max={1025}
          step={1}
          low={filters.dexMin}
          high={filters.dexMax}
          onLow={(dexMin) => set({ dexMin })}
          onHigh={(dexMax) => set({ dexMax })}
        />
        <div>
          <label htmlFor={`${idPrefix}-color`}>Color</label>
          <select id={`${idPrefix}-color`} value={filters.color} onChange={(event) => set({ color: event.target.value })}>
            <option value="">Any color</option>
            {COLORS.map((color) => (
              <option key={color} value={color}>{title(color)}</option>
            ))}
          </select>
        </div>
      </div>

      <fieldset className="check-row">
        <legend>Megas</legend>
        <div className="mode-toggle" role="group" aria-label="Mega evolutions">
          <button
            type="button"
            className={!filters.megas ? 'choice-btn is-on' : 'choice-btn'}
            aria-pressed={!filters.megas}
            onClick={() => set({ megas: '' })}
          >
            Show Megas
          </button>
          <button
            type="button"
            className={filters.megas === 'hide' ? 'choice-btn is-on' : 'choice-btn'}
            aria-pressed={filters.megas === 'hide'}
            onClick={() => set({ megas: 'hide' })}
          >
            Hide Megas
          </button>
          <button
            type="button"
            className={filters.megas === 'only' ? 'choice-btn is-on' : 'choice-btn'}
            aria-pressed={filters.megas === 'only'}
            onClick={() => set({ megas: 'only' })}
          >
            Only Megas
          </button>
        </div>
      </fieldset>

      <fieldset className="check-row">
        <legend>Basic info</legend>
        {CATEGORIES.map(([id, text]) => (
          <label key={id} className="check-label">
            <input
              type="checkbox"
              checked={filters.categories.includes(id)}
              onChange={() => set({ categories: toggleItem(filters.categories, id) })}
            />
            <span>{text}</span>
          </label>
        ))}
      </fieldset>

      <div className="filter-grid">
        <RangePair
          id={`${idPrefix}-height`}
          label="Height (m)"
          min={0}
          max={20}
          step={0.1}
          low={filters.heightMin}
          high={filters.heightMax}
          onLow={(heightMin) => set({ heightMin })}
          onHigh={(heightMax) => set({ heightMax })}
        />
        <RangePair
          id={`${idPrefix}-weight`}
          label="Weight (kg)"
          min={0}
          max={1000}
          step={0.1}
          low={filters.weightMin}
          high={filters.weightMax}
          onLow={(weightMin) => set({ weightMin })}
          onHigh={(weightMax) => set({ weightMax })}
        />
      </div>

      {filters.mode === 'advanced' && (
        <div className="advanced-block">
          <div className="filter-grid">
            <div>
              <label htmlFor={`${idPrefix}-sort`}>Sort</label>
              <select
                id={`${idPrefix}-sort`}
                value={filters.sort}
                onChange={(event) => set({ sort: event.target.value })}
              >
                <option value="dex">Dex number</option>
                {STAT_FIELDS.map(([field, text]) => (
                  <option key={field} value={field}>{text}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor={`${idPrefix}-dir`}>Order</label>
              <select
                id={`${idPrefix}-dir`}
                value={filters.sortDir}
                onChange={(event) => set({ sortDir: event.target.value })}
              >
                <option value="asc">Low to high</option>
                <option value="desc">High to low</option>
              </select>
            </div>
          </div>
          {STAT_FIELDS.map(([field, text, param]) => (
            <RangePair
              key={field}
              id={`${idPrefix}-${param}`}
              label={text}
              min={0}
              max={param === 'bst' ? 800 : 255}
              step={1}
              low={filters.statMin[param] || ''}
              high={filters.statMax[param] || ''}
              onLow={(value) => set({ statMin: { ...filters.statMin, [param]: value } })}
              onHigh={(value) => set({ statMax: { ...filters.statMax, [param]: value } })}
            />
          ))}
          <TokenPicker
            id={`${idPrefix}-ability`}
            label="Abilities"
            options={abilityOptions}
            selected={filters.abilities}
            mode={filters.abilityMode}
            onMode={(abilityMode) => set({ abilityMode })}
            onChange={(abilities) => set({ abilities })}
            placeholder="Levitate"
          />
          <TokenPicker
            id={`${idPrefix}-move`}
            label="Moves"
            options={moveOptions}
            selected={filters.moves}
            mode={filters.moveMode}
            onMode={(moveMode) => set({ moveMode })}
            onChange={(moves) => set({ moves })}
            placeholder={learnState === 'loading' ? 'Loading moves…' : 'Earthquake'}
          />
          {learnState === 'error' && (
            <p className="status" role="status">Move list could not be loaded. Other filters still work.</p>
          )}
          {learnState === 'loading' && <p className="status" role="status">Loading the move list…</p>}
        </div>
      )}

      {typeof onPick === 'function' && (
        <div className="pick-block">
          <p className="filter-label">Pick a result</p>
          {picked.length === 0 && <p className="status">No Pokémon match these filters.</p>}
          <ul className="pick-list">
            {picked.slice(0, 24).map((entry) => (
              <li key={entry.slug || entry.id}>
                <button type="button" className="pick-btn" onClick={() => onPick(entry)}>
                  {entry.sprite ? <img src={entry.sprite} alt="" width="36" height="36" /> : null}
                  <span className="suggest-name">{entry.name}</span>
                  <span className="suggest-no">{formatDexNumber(entry.id)}</span>
                </button>
              </li>
            ))}
          </ul>
          {picked.length > 24 && <p className="status">Showing 24 of {picked.length}. Narrow the filters to see fewer.</p>}
        </div>
      )}
    </form>
  );
}

export default DexFilterPanel;
